import React from 'react';
import styled from 'styled-components';
import { isEmpty, map, truncate } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { push as Push } from 'redux-first-history';
import { Campaign, CampaignTypeFilter } from '@dev/ee-api/ee-api-models';
import { ContentLoader, EmptyState, MobileCardRowsProps, MobileTable, palette } from '@dev/ee-components';
import { safelyEncodeBase64Url } from '@dev/ee-tools';

import { CopyCampaignsByNameStart, DeleteCampaigns, getCampaignsStore } from '@/store/campaigns/actions';
import { CampaignsPaths, pathsParams } from '@/services/dictionaries/Paths';
import { getCampaignCreatorType } from '@/services/tools/getCampaignCreatorType';
import { ShowDialog } from '@/store/dialog/actions';
import { DropdownOptions } from '@/store/campaigns/types';
import { getConfigStore } from '@/store/config/types';
import { StatusBadge } from '@/assets/StatusBadge';

const TableContainer = styled.div`
    margin-top: 24px;

    & > div {
        padding-bottom: 2px;
    }
`;
const TitleWrapper = styled.div`
    word-break: break-all;
`;

const useTableHeaders = (): string[] => {
    const { t } = useTranslation();

    return [t('status'), t('opened'), t('clicked')];
};

const useTableRows = (): MobileCardRowsProps[] => {
    const { t } = useTranslation();
    const campaigns = useSelector(getCampaignsStore);

    const SkeletonTemplate = {
        tableTitle: <ContentLoader mode="row" />,
        status: <ContentLoader mode="row" />,
    };

    const SkeletonRows = map(
        [...new Array(campaigns?.pagination?.perPage)],
        (): MobileCardRowsProps => SkeletonTemplate
    );

    const rows = map(
        campaigns?.collection,
        (campaigns: Campaign, index): MobileCardRowsProps => ({
            tableTitle: <TitleWrapper>{truncate(campaigns.Name, { length: 45 })}</TitleWrapper>,
            Status: <StatusBadge key={`status-${index}`} value={t('draft') as string} variant="Draft" />,
            returnOnItemClick: campaigns as any,
        })
    );
    return campaigns.loading ? SkeletonRows : rows;
};

const MobileDraftsTable = (): JSX.Element => {
    const campaigns = useSelector(getCampaignsStore);
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const headers = useTableHeaders();
    const rows = useTableRows();

    const config = useSelector(getConfigStore);

    const screenCampaignType = [CampaignTypeFilter.Draft];

    const options = [
        {
            label: t('edit'),
            value: DropdownOptions.Edit,
        },
        {
            label: t('copycampaign'),
            value: DropdownOptions.CopyCampaign,
        },
        {
            label: t('delete'),
            color: palette.red[6],
            value: DropdownOptions.Delete,
        },
    ];

    if (isEmpty(campaigns?.collection) && !campaigns?.loading) {
        return <EmptyState text={t('thereisnodatatodisplay')} />;
    }

    const copyCampaign = (name: string) => {
        dispatch(
            ShowDialog({
                title: t('copycampaign'),
                body: `${t('copycampaignconfirmation')} "${name}"?`,
                confirmLabel: t('copy'),
                cancelLabel: t('cancel'),
                onClickConfirm: () => dispatch(CopyCampaignsByNameStart(name)),
            })
        );
    };

    const handleDropdownOptions = (_e, option, campaign: Campaign): void => {
        switch (option.value) {
            case DropdownOptions.ViewDetails:
            case DropdownOptions.Edit:
                if (config.isStandalone) {
                    const campaignCreatorType = getCampaignCreatorType(campaign.Type);
                    dispatch(
                        Push(
                            `${CampaignsPaths.CampaignCreatorName.replace(
                                pathsParams.type,
                                campaignCreatorType
                            ).replace(pathsParams.name, safelyEncodeBase64Url(campaign.Name))}`
                        )
                    );
                } else {
                    window.location.hash = `${CampaignsPaths.OldViewCampaign.replace(
                        ':name',
                        safelyEncodeBase64Url(campaign.Name)
                    )}`;
                }
                break;
            case DropdownOptions.CopyCampaign:
                copyCampaign(campaign.Name);
                break;
            case DropdownOptions.Delete:
                dispatch(DeleteCampaigns([campaign.Name], screenCampaignType));
                break;
            default:
                break;
        }
    };

    return (
        <TableContainer>
            <MobileTable headers={headers} rows={rows} dropdownData={options} onItemClick={handleDropdownOptions} />
        </TableContainer>
    );
};

export default MobileDraftsTable;
